import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { axiosGet } from '../utils/axiosGet';
import { getCollectionRowNames } from '../utils/getCollectionRowNames';
import { addCmseCommitteeToSectionalCommitteeArray } from '../utils/addCmseCommitteeToSectionalCommitteeArray';
import { sortByCustomOrder } from '../utils/customOrder';
import { DEFAULT_PAGINATION_LIMIT } from '../utils/constants';
import { useLoggedInUserContextProvider } from './LoggedInUserContext';
import { devError } from '../utils/devLog';

const StaticCollections = createContext();

const initialState = {
  activeFellows: {},
  activeFellowsArray: [],
  australianStatus: {},
  binariesTypes: {},
  binariesTypesArray: [],
  coiTypes: {},
  coiTypesArray: [],
  declineReasons: {},
  electionTypes: {},
  electionTypesArray: [],
  fellows: {},
  fellowsArray: [],
  formStepsArray: [],
  genderTypes: {},
  genderTypesArray: [],
  nonAasReferees: {},
  nonAasRefereesArray: [],
  nominationHistoryArray: [],
  nominationStatusTypes: {},
  nominationZipYears: [],
  nonAdminNominationStatusTypes: {},
  organisations: {},
  organisationsArray: [],
  refereeReportArchivedArray: [],
  refereeStatuses: {},
  refereeActionsArray: [],
  sectionalCommittees: {},
  sectionalCommitteeHistoryArray: [],
  nominationRefereeHistoryArray: [],
  sectionalCommitteesWithCmse: {},
  sectionalCommitteesArray: [],
  stateTerritories: {},
  supporterTypes: {},
  supporterTypesArray: [],
  titlesTypes: {},
  titlesTypesArray: [],
  users: {},
  usersArray: [],
  isLoading: true,
  error: null,
};

// Reducer to handle state updates
const staticCollectionsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'LOAD_COMPLETE':
      return { ...action.payload, isLoading: false, error: null };
    case 'ADD_NON_AAS_REFEREE':
      return {
        ...state,
        nonAasRefereesArray: [
          ...state.nonAasRefereesArray,
          action.payload.referee,
        ],
        nonAasReferees: {
          ...state.nonAasReferees,
          ...getCollectionRowNames([action.payload.referee]),
        },
      };
    case 'UPDATE_USERS':
      return {
        ...state,
        users: { ...state.users, ...action.payload.users },
        usersArray: (() => {
          const existingIds = new Set(state.usersArray.map((u) => u.id));
          const usersToAdd = action.payload.usersArray.filter(
            (u) => !existingIds.has(u.id),
          );

          const updatedArray = state.usersArray.map((user) => {
            const updatedUser = action.payload.usersArray.find(
              (u) => u.id === user.id,
            );
            return updatedUser || user;
          });

          return [...updatedArray, ...usersToAdd];
        })(),
      };
    default:
      return state;
  }
};

export const StaticCollectionsProvider = ({ children }) => {
  const paginationLimit = `&pagination[limit]=${DEFAULT_PAGINATION_LIMIT}`;
  const [state, dispatch] = useReducer(staticCollectionsReducer, initialState);

  const { originalUserDetails } = useLoggedInUserContextProvider();

  const memoizedUserId = useMemo(
    () => originalUserDetails?.id,
    [originalUserDetails?.id],
  );

  useEffect(() => {
    if (!memoizedUserId) return;

    const loadAllStaticData = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const allRequests = await Promise.all([
          axiosGet(
            'fellows?filters[active][$eq]=true&filters[doNotContact][$eq]=false&sort=lastName',
          ),
          axiosGet('australian-statuses'),
          axiosGet('binaries'),
          axiosGet('coi-types'),
          axiosGet('election-types'),
          axiosGet(`fellows?sort=lastName${paginationLimit}`),
          axiosGet(
            `form-steps?filters[formName][$eq]=nomination&populate[formField][populate][0]=selectOption&populate[formField][populate][1]=fileExtension`,
          ),
          axiosGet('genders'),
          axiosGet(`non-aas-referees?sort=lastName${paginationLimit}`),
          axiosGet('nomination-histories'),
          axiosGet('nomination-status-types'),
          axiosGet('nomination-zip/years'),
          axiosGet('organisations?sort=name'),
          axiosGet(`referee-report-archives?populate=*&${paginationLimit}`),
          axiosGet('referee-actions'),
          axiosGet('referee-statuses'),
          axiosGet('referee-report-decline-reasons'),
          axiosGet('sectional-committees'),
          axiosGet('sectional-committee-histories'),
          axiosGet('nomination-referee-histories'),
          axiosGet('state-territories'),
          axiosGet('supporter-types'),
          axiosGet('titles'),
          axiosGet('users'),
        ]);

        const [
          activeFellowsRes,
          australianStatusRes,
          binariesRes,
          coiTypesRes,
          electionTypesRes,
          fellowsRes,
          formStepsRes,
          gendersRes,
          nonAasRefereesRes,
          nominationHistoryRes,
          nominationStatusRes,
          nominationZipRes,
          organisationsRes,
          refereeReportArchiveRes,
          refereeActionsRes,
          refereeStatusesRes,
          declineReasonsRes,
          sectionalCommitteesRes,
          sectionalCommitteeHistoryRes,
          nominationRefereeHistoryRes,
          stateTerritoriesRes,
          supporterTypesRes,
          titlesRes,
          usersRes,
        ] = allRequests;

        const customOrder = [2, 3, 1];
        const electionTypesArrayProcessed = sortByCustomOrder(
          electionTypesRes?.data?.data,
          customOrder,
        );

        const organisationsAndNotListedOption = [
          ...(organisationsRes?.data?.data || []),
          {
            id: 0,
            attributes: {
              name: '-- My organisation is not listed --',
            },
          },
        ];

        const resultGetCollectionRowNames = getCollectionRowNames(
          nominationStatusRes?.data?.data,
        );
        const proposerNominationStatusTypes = Object.entries(
          resultGetCollectionRowNames,
        ).reduce((acc, [key, value]) => {
          acc[key] = value === 'Shortlisted' ? 'Submitted' : value;
          return acc;
        }, {});

        const completeData = {
          activeFellows:
            getCollectionRowNames(activeFellowsRes?.data?.data) || {},
          activeFellowsArray: activeFellowsRes?.data?.data || [],
          australianStatus:
            getCollectionRowNames(australianStatusRes?.data?.data) || {},
          binariesTypes: getCollectionRowNames(binariesRes?.data?.data) || {},
          binariesTypesArray: binariesRes?.data?.data || [],
          coiTypes: getCollectionRowNames(coiTypesRes?.data?.data) || {},
          coiTypesArray: coiTypesRes?.data?.data || [],
          declineReasons:
            getCollectionRowNames(declineReasonsRes?.data?.data) || {},
          electionTypes:
            getCollectionRowNames(electionTypesRes?.data?.data) || {},
          electionTypesArray: electionTypesArrayProcessed || [],
          fellows: getCollectionRowNames(fellowsRes?.data?.data) || {},
          fellowsArray: fellowsRes?.data?.data || [],
          formStepsArray: formStepsRes?.data?.data || [],
          genderTypes: getCollectionRowNames(gendersRes?.data?.data) || {},
          genderTypesArray: gendersRes?.data?.data || [],
          nonAasReferees:
            getCollectionRowNames(nonAasRefereesRes?.data?.data) || {},
          nonAasRefereesArray: nonAasRefereesRes?.data?.data || [],
          nominationHistoryArray: nominationHistoryRes?.data?.data || [],
          nominationStatusTypes: resultGetCollectionRowNames || {},
          nominationZipYears: nominationZipRes?.data || [],
          nonAdminNominationStatusTypes: proposerNominationStatusTypes || {},
          organisations:
            getCollectionRowNames(organisationsAndNotListedOption) || {},
          organisationsArray: organisationsAndNotListedOption || [],
          refereeReportArchivedArray: refereeReportArchiveRes?.data?.data || [],
          refereeStatuses:
            getCollectionRowNames(refereeStatusesRes?.data?.data) || {},
          refereeActionsArray: refereeActionsRes?.data?.data || [],
          sectionalCommittees:
            getCollectionRowNames(sectionalCommitteesRes?.data?.data) || {},
          sectionalCommitteeHistoryArray:
            sectionalCommitteeHistoryRes?.data?.data || [],
          nominationRefereeHistoryArray:
            nominationRefereeHistoryRes?.data?.data || [],
          sectionalCommitteesWithCmse:
            getCollectionRowNames(
              addCmseCommitteeToSectionalCommitteeArray(
                sectionalCommitteesRes?.data?.data,
              ),
            ) || {},
          sectionalCommitteesArray: sectionalCommitteesRes?.data?.data || [],
          stateTerritories:
            getCollectionRowNames(stateTerritoriesRes?.data?.data) || {},
          supporterTypes:
            getCollectionRowNames(supporterTypesRes?.data?.data) || {},
          supporterTypesArray: supporterTypesRes?.data?.data || [],
          titlesTypes: getCollectionRowNames(titlesRes?.data?.data) || {},
          titlesTypesArray: titlesRes?.data?.data || [],
          users: getCollectionRowNames(usersRes?.data) || {},
          usersArray: usersRes?.data || [],
        };

        dispatch({ type: 'LOAD_COMPLETE', payload: completeData });
      } catch (error) {
        devError('Failed to load static collections:', error);
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    };

    loadAllStaticData();
  }, [memoizedUserId, paginationLimit]);

  const addNonAasReferee = useCallback((referee) => {
    dispatch({
      type: 'ADD_NON_AAS_REFEREE',
      payload: { referee },
    });
  }, []);

  const refreshUsersByNonAasReferees = useCallback(
    async (nonAasRefereeIds) => {
      if (
        !memoizedUserId ||
        !Array.isArray(nonAasRefereeIds) ||
        nonAasRefereeIds.length === 0
      ) {
        return;
      }

      try {
        const CHUNK = 100;
        const updatedUsersArray = [];
        for (let i = 0; i < nonAasRefereeIds.length; i += CHUNK) {
          const slice = nonAasRefereeIds.slice(i, i + CHUNK);
          const inQuery = slice
            .map((id, j) => `filters[nonAasRefereeId][$in][${j}]=${id}`)
            .join('&');
          const batchResponse = await axiosGet(
            `users?${inQuery}&pagination[pageSize]=${DEFAULT_PAGINATION_LIMIT}`,
          );
          if (Array.isArray(batchResponse?.data)) {
            updatedUsersArray.push(...batchResponse.data);
          }
        }

        if (updatedUsersArray.length === 0) {
          return;
        }

        const updatedUsers = getCollectionRowNames(updatedUsersArray);

        dispatch({
          type: 'UPDATE_USERS',
          payload: {
            users: updatedUsers,
            usersArray: updatedUsersArray,
          },
        });
      } catch (error) {
        devError('Error refreshing users by non-AAS referees:', error);
      }
    },
    [memoizedUserId],
  );

  const contextValue = useMemo(
    () => ({
      ...state,
      addNonAasReferee,
      refreshUsersByNonAasReferees,
    }),
    [state, addNonAasReferee, refreshUsersByNonAasReferees],
  );

  return (
    <StaticCollections.Provider value={contextValue}>
      {children}
    </StaticCollections.Provider>
  );
};

StaticCollectionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useStaticCollections = () => useContext(StaticCollections);
